<template>
  <div class="modal_page center" @click.self="close">
    <div class="modal_window col gap-20">
      <div class="row align-center space-between">
        <h2 class="font-24 weight-600">Propose Alternative Time</h2>
        <s-icon color="grey" @click="close" class="pointer">
          close
        </s-icon>
      </div>

      <p class="font-14 grey--text">
        Propose a different time for the meeting request from {{ request?.requesterName }}
      </p>

      <div class="col gap-15">
        <div class="row gap-10 align-start sm-col">
          <div class="col gap-5 grow">
            <label class="font-14 weight-600">Date *</label>
            <s-date-picker :prefill="datePrefill" :noedit="true" block @changeDate="handleDateChange" />
          </div>
          <div class="col gap-5 grow">
            <label class="font-14 weight-600">Start Time *</label>
            <s-time-picker :prefill="startTimePrefill" :noedit="true" block @changeTime="handleStartTimeChange" />
          </div>
        </div>

        <div v-if="meetingDurationMinutes > 0" class="font-14 grey--text">
          Meeting length: <span class="weight-600 black--text">{{ meetingDurationMinutes }} min</span>
        </div>
      </div>

      <div v-if="error" class="red--text font-14">{{ error }}</div>

      <div class="row gap-10 justify-end mt-10">
        <s-btn @click="close" class="shade2" height="45">
          Cancel
        </s-btn>
        <s-btn 
          @click="submitCounterProposal" 
          class="primary white--text"
          height="45"
          :disabled="!isValid || submitting"
        >
          <pulse-loader v-if="submitting" :loading="true" color="white" size="8px" />
          <span v-else>Propose Time</span>
        </s-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';

const store = useStore();

const props = defineProps({
  request: Object
});

const selectedDate = ref(null);
const startTime = ref(null);
const error = ref('');
const submitting = ref(false);

const datePrefill = computed(() => {
  if (!selectedDate.value) return null;
  return dayjs(selectedDate.value).format('MMM D, YYYY');
});

const startTimePrefill = computed(() => {
  if (!startTime.value) return null;
  return dayjs(`2000-01-01 ${startTime.value}`, 'YYYY-MM-DD HH:mm').format('hh:mm A');
});

const meetingDurationMinutes = computed(() => {
  const req = props.request;
  if (!req?.requestedFrom || !req?.requestedTo) return 0;
  const secs = req.requestedTo - req.requestedFrom;
  if (secs <= 0) return 0;
  return Math.round(secs / 60);
});

const handleDateChange = (dateObj) => {
  if (dateObj && typeof dateObj.format === 'function') {
    selectedDate.value = dateObj.format('YYYY-MM-DD');
  }
};

const handleStartTimeChange = (timeObj) => {
  if (timeObj && typeof timeObj.format === 'function') {
    startTime.value = timeObj.format('HH:mm');
  }
};

const isValid = computed(() => {
  return selectedDate.value && startTime.value && meetingDurationMinutes.value > 0;
});

const close = () => {
  store.commit('modals/counterProposeModal', { open: false, request: null });
};

const submitCounterProposal = async () => {
  if (!isValid.value) {
    error.value = 'Please fill in all fields';
    return;
  }

  const dur = meetingDurationMinutes.value;
  if (!dur) {
    error.value = 'Could not determine meeting duration for this request';
    return;
  }

  const date = dayjs(selectedDate.value);
  const start = dayjs(`${date.format('YYYY-MM-DD')} ${startTime.value}`);
  const end = start.add(dur, 'minute');

  submitting.value = true;
  error.value = '';

  try {
    const response = await store.dispatch('booking/counterPropose', {
      requestId: props.request.id,
      newFrom: start.unix(),
      newTo: end.unix()
    });

    if (response && response.status === 'ok') {
      close();
      await store.dispatch('booking/fetchRequests');
    } else {
      error.value = response?.message || 'Failed to propose alternative time';
    }
  } catch (e) {
    error.value = 'An error occurred. Please try again.';
  } finally {
    submitting.value = false;
  }
};

watch(() => props.request, (newRequest) => {
  if (newRequest) {
    const baseFrom = newRequest.status === 'counter_proposed' && newRequest.counterProposedFrom
      ? newRequest.counterProposedFrom
      : newRequest.requestedFrom;
    const baseTime = dayjs.unix(baseFrom);
    selectedDate.value = baseTime.format('YYYY-MM-DD');
    startTime.value = baseTime.format('HH:mm');
  }
}, { immediate: true });
</script>

<style scoped>
.modal_page {
  position: fixed;
  left: 0;
  top: 0px;
  width: 100vw;
  height: 100vh;
  background: rgba(41, 41, 41, 0.6);
  z-index: 100;
}

.modal_window {
  width: 500px;
  padding: 30px;
  padding-left: 60px;
  padding-right: 60px;
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.35);
  background-color: var(--shade1);
  border-radius: 10px;
}

@media only screen and (max-width: 576px) {
  .modal_window {
    padding-left: 20px;
    padding-right: 20px;
    width: 100%;
  }
}
</style>


